import React, { useCallback, useEffect, useState } from 'react'
import UserLayout from '../../layouts/UserLayout'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { getFromStorage } from '../../utils/storage'
import axios from 'axios'
import { api } from '../../utils/api'
import { handleError } from '../../utils/error'
import { currentUserActions } from '../../redux/store'
import { 
	Box,
	Button,
	Table,
	TableBody,
	TableCell,
	TableHead, 
	TableRow, 
	Typography,
	Paper,
	Container,
	IconButton,
	Tooltip,
} from '@mui/material'
import { 
	Refresh as RefreshIcon,
	Analytics as AnalyticsIcon,
	Storefront as StorefrontIcon,
	TrendingUp as TrendingUpIcon
} from '@mui/icons-material'

export default function StockForecast() {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const token = getFromStorage('current-user')?.accessToken
	const { currentUser } = useSelector((state) => state.currentUser)
	const [forecastStock, setForecastStock] = useState([])
	const [isLoading, setIsLoading] = useState(false)

	const getForecastStock = useCallback(
		async function () {
			try {
				setIsLoading(true)
				const response = await axios({
					url: api.GET_FORECAST_STOCK,
					method: 'GET',
					headers: {
						Authorization: `Bearer ${token}`,
					},
				})
				setForecastStock(response.data)
			} catch (err) {
				handleError(err)
				dispatch(currentUserActions.isLogout())
				navigate('/login')
			} finally {
				setIsLoading(false)
			}
		},
		[token, dispatch, navigate],
	)

	useEffect(() => {
		getForecastStock()
	}, [getForecastStock])

	const handleRefresh = () => {
		getForecastStock()
	}

	return (
		<UserLayout>
			<Container maxWidth="xl" sx={{ py: 3 }}>
				{/* Header Section */}
				<Paper
					elevation={0}
					sx={{
						background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
						borderRadius: '16px',
						padding: '24px',
						mb: 3,
						position: 'relative',
						overflow: 'hidden',
						'&::before': {
							content: '""',
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
							opacity: 0.3,
						}
					}}>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
							<Box
								sx={{
									background: 'rgba(255, 255, 255, 0.2)',
									borderRadius: '12px',
									padding: '12px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}>
								<TrendingUpIcon sx={{ color: '#fff', fontSize: 28 }} />
							</Box>
							<Box>
								<Typography
									variant="h4"
									sx={{
										color: '#fff',
										fontWeight: 700,
										letterSpacing: '1px',
										textShadow: '0 2px 4px rgba(0,0,0,0.3)',
										mb: 0.5,
									}}>
									DỰ BÁO TỒN KHO
								</Typography>
								<Typography
									variant="body2"
									sx={{
										color: 'rgba(255, 255, 255, 0.9)',
										fontWeight: 500,
									}}>
									Phân tích và dự báo nhu cầu tồn kho cho các cửa hàng
								</Typography>
							</Box>
						</Box>
						<Box sx={{ display: 'flex', gap: 2 }}>
							<Tooltip title="Làm mới dữ liệu">
								<IconButton
									onClick={handleRefresh}
									disabled={isLoading}
									sx={{
										background: 'rgba(255, 255, 255, 0.2)',
										color: '#fff',
										'&:hover': {
											background: 'rgba(255, 255, 255, 0.3)',
											transform: 'scale(1.05)',
										},
										transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
									}}>
									<RefreshIcon />
								</IconButton>
							</Tooltip>
							{currentUser.permission?.includes('manageStock') && (
								<Button
									variant='contained'
									startIcon={<StorefrontIcon />}
									sx={{
										background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
										color: '#fff',
										borderRadius: '12px',
										px: 3,
										py: 1.5,
										fontWeight: 600,
										textTransform: 'none',
										boxShadow: '0 4px 16px rgba(255, 107, 107, 0.3)',
										'&:hover': {
											background: 'linear-gradient(135deg, #FF5252 0%, #FF7043 100%)',
											transform: 'translateY(-2px)',
											boxShadow: '0 8px 24px rgba(255, 107, 107, 0.4)',
										},
										transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
									}}
									onClick={() => navigate('/admin/stock')}>
									Kho tổng
								</Button>
							)}
						</Box>
					</Box>
				</Paper>

				{/* Statistics Cards */}
				<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
					<Paper
						elevation={0}
						sx={{
							background: 'rgba(255, 255, 255, 0.95)',
							backdropFilter: 'blur(20px)',
							borderRadius: '16px',
							padding: '20px',
							border: '1px solid rgba(76, 175, 80, 0.1)',
							boxShadow: '0 4px 16px rgba(76, 175, 80, 0.1)',
						}}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
							<Box
								sx={{
									background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
									borderRadius: '12px',
									padding: '10px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}>
								<AnalyticsIcon sx={{ color: '#fff', fontSize: 24 }} />
							</Box>
							<Box>
								<Typography variant="h6" sx={{ fontWeight: 700, color: '#4CAF50', mb: 0.5 }}>
									{forecastStock?.length || 0}
								</Typography>
								<Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
									Cửa hàng
								</Typography>
							</Box>
						</Box>
					</Paper>

					<Paper
						elevation={0}
						sx={{
							background: 'rgba(255, 255, 255, 0.95)',
							backdropFilter: 'blur(20px)',
							borderRadius: '16px',
							padding: '20px',
							border: '1px solid rgba(255, 107, 107, 0.1)',
							boxShadow: '0 4px 16px rgba(255, 107, 107, 0.1)',
						}}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
							<Box
								sx={{
									background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
									borderRadius: '12px',
									padding: '10px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}>
								<TrendingUpIcon sx={{ color: '#fff', fontSize: 24 }} />
							</Box>
							<Box>
								<Typography variant="h6" sx={{ fontWeight: 700, color: '#FF6B6B', mb: 0.5 }}>
									{forecastStock?.reduce((sum, store) => (sum += store.averageQuantityPerDay || 0), 0)?.toFixed(1) || '0.0'}
								</Typography>
								<Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
									Bán chín TB/ngày
								</Typography>
							</Box>
						</Box>
					</Paper>

					<Paper
						elevation={0}
						sx={{
							background: 'rgba(255, 255, 255, 0.95)',
							backdropFilter: 'blur(20px)',
							borderRadius: '16px',
							padding: '20px',
							border: '1px solid rgba(76, 175, 80, 0.1)',
							boxShadow: '0 4px 16px rgba(76, 175, 80, 0.1)',
						}}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
							<Box
								sx={{
									background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
									borderRadius: '12px',
									padding: '10px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}>
								<StorefrontIcon sx={{ color: '#fff', fontSize: 24 }} />
							</Box>
							<Box>
								<Typography variant="h6" sx={{ fontWeight: 700, color: '#4CAF50', mb: 0.5 }}>
									{forecastStock?.reduce((sum, store) => (sum += (store?.freshInventory * 94) / 100 + (store?.cookedInventory || 0)), 0)?.toFixed(1) || '0.0'}
								</Typography>
								<Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
									Tồn kho hiện tại
								</Typography>
							</Box>
						</Box>
					</Paper>

					<Paper
						elevation={0}
						sx={{
							background: 'rgba(255, 255, 255, 0.95)',
							backdropFilter: 'blur(20px)',
							borderRadius: '16px',
							padding: '20px',
							border: '1px solid rgba(255, 107, 107, 0.1)',
							boxShadow: '0 4px 16px rgba(255, 107, 107, 0.1)',
						}}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
							<Box
								sx={{
									background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
									borderRadius: '12px',
									padding: '10px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}>
								<TrendingUpIcon sx={{ color: '#fff', fontSize: 24 }} />
							</Box>
							<Box>
								<Typography variant="h6" sx={{ fontWeight: 700, color: '#FF6B6B', mb: 0.5 }}>
									{forecastStock?.reduce((sum, store) => {
										const need = ((store?.averageQuantityPerDay * 2 - ((store?.freshInventory * 94) / 100 + (store?.cookedInventory || 0))) * 100) / 94
										return sum + (need > 0 ? need : 0)
									}, 0)?.toFixed(1) || '0.0'}
								</Typography>
								<Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
									Nhu cầu 2 ngày
								</Typography>
							</Box>
						</Box>
					</Paper>
				</Box>

				{/* Main Table */}
				<Paper
					elevation={0}
					sx={{
						background: 'rgba(255, 255, 255, 0.98)',
						backdropFilter: 'blur(20px)',
						borderRadius: '16px',
						border: '1px solid rgba(76, 175, 80, 0.1)',
						boxShadow: '0 8px 32px rgba(76, 175, 80, 0.1)',
					}}>
					<Box sx={{ p: 3, borderBottom: '1px solid rgba(76, 175, 80, 0.1)' }}>
						<Typography variant="h6" sx={{ fontWeight: 600, color: '#4CAF50' }}>
							Chi tiết dự báo theo cửa hàng
						</Typography>
					</Box>
					<Box sx={{ overflow: 'auto' }}>
						<Table sx={{ minWidth: 800 }}>
							<TableHead>
								<TableRow sx={{ background: '#4CAF50' }}>
									<TableCell
										rowSpan={2}
										sx={{
											color: '#fff',
											fontWeight: 700,
											borderRight: '1px solid rgba(255, 255, 255, 0.2)',
											minWidth: 120,
											textAlign: 'center',
											padding: '16px 8px',
										}}>
										Cửa hàng
									</TableCell>
									<TableCell
										rowSpan={2}
										sx={{
											color: '#fff',
											fontWeight: 700,
											borderRight: '1px solid rgba(255, 255, 255, 0.2)',
											minWidth: 140,
											textAlign: 'center',
											padding: '16px 8px',
										}}>
										Bán chín TB
									</TableCell>
									<TableCell
										colSpan={3}
										sx={{
											color: '#fff',
											fontWeight: 700,
											borderRight: '1px solid rgba(255, 255, 255, 0.2)',
											minWidth: 140,
											textAlign: 'center',
											padding: '16px 8px',
										}}>
										Bán chín hôm nay
									</TableCell>
									<TableCell
										colSpan={3}
										sx={{
											color: '#fff',
											fontWeight: 700,
											borderRight: '1px solid rgba(255, 255, 255, 0.2)',
											minWidth: 140,
											textAlign: 'center',
											padding: '16px 8px',
										}}>
										Tồn kho hiện tại
									</TableCell>
									<TableCell
										rowSpan={2}
										sx={{
											color: '#fff',
											fontWeight: 700,
											minWidth: 160,
											textAlign: 'center',
											padding: '16px 8px',
										}}>
										Nhu cầu 2 ngày
									</TableCell>
								</TableRow>
								<TableRow sx={{ background: '#4CAF50' }}>
									<TableCell
										sx={{
											color: '#fff',
											fontWeight: 600,
											borderRight: '1px solid rgba(255, 255, 255, 0.2)',
											minWidth: 100,
											textAlign: 'center',
											padding: '12px 8px',
										}}>
										Sáng
									</TableCell>
									<TableCell
										sx={{
											color: '#fff',
											fontWeight: 600,
											borderRight: '1px solid rgba(255, 255, 255, 0.2)',
											minWidth: 100,
											textAlign: 'center',
											padding: '12px 8px',
										}}>
										Chiều
									</TableCell>
									<TableCell
										sx={{
											color: '#fff',
											fontWeight: 600,
											borderRight: '1px solid rgba(255, 255, 255, 0.2)',
											minWidth: 100,
											textAlign: 'center',
											padding: '12px 8px',
										}}>
										Cả ngày
									</TableCell>
									<TableCell
										sx={{
											color: '#fff',
											fontWeight: 600,
											borderRight: '1px solid rgba(255, 255, 255, 0.2)',
											minWidth: 100,
											textAlign: 'center',
											padding: '12px 8px',
										}}>
										Sống
									</TableCell>
									<TableCell
										sx={{
											color: '#fff',
											fontWeight: 600,
											borderRight: '1px solid rgba(255, 255, 255, 0.2)',
											minWidth: 100,
											textAlign: 'center',
											padding: '12px 8px',
										}}>
										Chín
									</TableCell>
									<TableCell
										sx={{
											color: '#fff',
											fontWeight: 600,
											borderRight: '1px solid rgba(255, 255, 255, 0.2)',
											minWidth: 100,
											textAlign: 'center',
											padding: '12px 8px',
										}}>
										Tổng (chín)
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{forecastStock?.map((item, index) => (
									<TableRow 
										key={index}
										sx={{
											'&:nth-of-type(even)': {
												background: 'rgba(76, 175, 80, 0.02)',
											},
											'&:hover': {
												background: 'rgba(76, 175, 80, 0.06)',
											},
											transition: 'background 0.2s ease',
										}}>
										<TableCell
											sx={{
												fontWeight: 600,
												color: '#2E7D32',
												borderRight: '1px solid rgba(76, 175, 80, 0.15)',
												textAlign: 'center',
												padding: '16px 8px',
											}}>
											{item?.store?.name}
										</TableCell>
										<TableCell
											sx={{
												fontWeight: 500,
												borderRight: '1px solid rgba(76, 175, 80, 0.15)',
												textAlign: 'center',
												padding: '16px 8px',
												color: '#424242',
											}}>
											{item?.averageQuantityPerDay?.toFixed(1)}
										</TableCell>
										<TableCell
											sx={{
												fontWeight: 500,
												borderRight: '1px solid rgba(76, 175, 80, 0.15)',
												textAlign: 'center',
												padding: '16px 8px',
												color: '#424242',
											}}>
											{item?.quantityTodayMorning?.toFixed(1)}
										</TableCell>
										<TableCell
											sx={{
												fontWeight: 500,
												borderRight: '1px solid rgba(76, 175, 80, 0.15)',
												textAlign: 'center',
												padding: '16px 8px',
												color: '#424242',
											}}>
											{item?.quantityTodayAfternoon?.toFixed(1)}
										</TableCell>
										<TableCell
											sx={{
												fontWeight: 500,
												borderRight: '1px solid rgba(76, 175, 80, 0.15)',
												textAlign: 'center',
												padding: '16px 8px',
												color: '#424242',
											}}>
											{(item?.quantityTodayMorning + item?.quantityTodayAfternoon)?.toFixed(1)}
										</TableCell>
										<TableCell
											sx={{
												fontWeight: 500,
												borderRight: '1px solid rgba(76, 175, 80, 0.15)',
												textAlign: 'center',
												padding: '16px 8px',
												color: '#424242',
											}}>
											{item?.freshInventory?.toFixed(1)}
										</TableCell>
										<TableCell
											sx={{
												fontWeight: 500,
												borderRight: '1px solid rgba(76, 175, 80, 0.15)',
												textAlign: 'center',
												padding: '16px 8px',
												color: '#424242',
											}}>
											{item?.cookedInventory?.toFixed(1)}
										</TableCell>
										<TableCell
											sx={{
												fontWeight: 500,
												borderRight: '1px solid rgba(76, 175, 80, 0.15)',
												textAlign: 'center',
												padding: '16px 8px',
												color: '#424242',
											}}>
											{((item?.freshInventory * 94) / 100 + item?.cookedInventory)?.toFixed(1)}
										</TableCell>
										<TableCell
											sx={{
												fontWeight: 600,
												textAlign: 'center',
												padding: '16px 8px',
												color: '#D32F2F',
											}}>
											{(((item?.averageQuantityPerDay * 2 - ((item?.freshInventory * 94) / 100 + item?.cookedInventory)) * 100) / 94 <= 0
												? 0
												: ((item?.averageQuantityPerDay * 2 - ((item?.freshInventory * 94) / 100 + item?.cookedInventory)) * 100) / 94)?.toFixed(1)}
										</TableCell>
									</TableRow>
								))}
								
								{/* Total Row */}
								<TableRow sx={{ 
									background: '#FF6B6B',
									'&:hover': {
										background: '#FF5252',
									},
									transition: 'background 0.3s ease',
								}}>
									<TableCell
										sx={{
											color: '#fff',
											fontWeight: 700,
											borderRight: '1px solid rgba(255, 255, 255, 0.2)',
											textAlign: 'center',
											padding: '20px 8px',
											fontSize: '1.1rem',
										}}>
										TỔNG
									</TableCell>
									<TableCell
										sx={{
											color: '#fff',
											fontWeight: 700,
											borderRight: '1px solid rgba(255, 255, 255, 0.2)',
											textAlign: 'center',
											padding: '20px 8px',
											fontSize: '1.1rem',
										}}>
										{forecastStock?.reduce((sum, store) => (sum += store.averageQuantityPerDay || 0), 0)?.toFixed(1)}
									</TableCell>
									<TableCell
										sx={{
											color: '#fff',
											fontWeight: 700,
											borderRight: '1px solid rgba(255, 255, 255, 0.2)',
											textAlign: 'center',
											padding: '20px 8px',
											fontSize: '1.1rem',
										}}>
										{forecastStock?.reduce((sum, store) => (sum += store.quantityTodayMorning || 0), 0)?.toFixed(1)}
									</TableCell>
									<TableCell
										sx={{
											color: '#fff',
											fontWeight: 700,
											borderRight: '1px solid rgba(255, 255, 255, 0.2)',
											textAlign: 'center',
											padding: '20px 8px',
											fontSize: '1.1rem',
										}}>
										{forecastStock?.reduce((sum, store) => (sum += store.quantityTodayAfternoon || 0), 0)?.toFixed(1)}
									</TableCell>
									<TableCell
										sx={{
											color: '#fff',
											fontWeight: 700,
											borderRight: '1px solid rgba(255, 255, 255, 0.2)',
											textAlign: 'center',
											padding: '20px 8px',
											fontSize: '1.1rem',
										}}>
										{forecastStock?.reduce((sum, store) => (sum += (store.quantityTodayMorning || 0) + (store.quantityTodayAfternoon || 0)), 0)?.toFixed(1)}
									</TableCell>
									<TableCell
										sx={{
											color: '#fff',
											fontWeight: 700,
											borderRight: '1px solid rgba(255, 255, 255, 0.2)',
											textAlign: 'center',
											padding: '20px 8px',
											fontSize: '1.1rem',
										}}>
										{forecastStock?.reduce((sum, store) => (sum += store.freshInventory || 0), 0)?.toFixed(1)}
									</TableCell>
									<TableCell
										sx={{
											color: '#fff',
											fontWeight: 700,
											borderRight: '1px solid rgba(255, 255, 255, 0.2)',
											textAlign: 'center',
											padding: '20px 8px',
											fontSize: '1.1rem',
										}}>
										{forecastStock?.reduce((sum, store) => (sum += store.cookedInventory || 0), 0)?.toFixed(1)}
									</TableCell>
									<TableCell
										sx={{
											color: '#fff',
											fontWeight: 700,
											borderRight: '1px solid rgba(255, 255, 255, 0.2)',
											textAlign: 'center',
											padding: '20px 8px',
											fontSize: '1.1rem',
										}}>
										{forecastStock?.reduce((sum, store) => (sum += (store?.freshInventory * 94) / 100 + (store?.cookedInventory || 0)), 0)?.toFixed(1)}
									</TableCell>
									<TableCell
										sx={{
											color: '#fff',
											fontWeight: 700,
											textAlign: 'center',
											padding: '20px 8px',
											fontSize: '1.1rem',
										}}>
										{forecastStock?.reduce((sum, store) => {
											const need = ((store?.averageQuantityPerDay * 2 - ((store?.freshInventory * 94) / 100 + (store?.cookedInventory || 0))) * 100) / 94
											return sum + (need > 0 ? need : 0)
										}, 0)?.toFixed(1)}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</Box>
				</Paper>
			</Container>
		</UserLayout>
	)
}
